"use client";

/**
 * CourierTrackMap — in-site live tracking for the send-in Uber courier.
 *
 * Polls /api/service-pickup/[id]/courier every 15s and plots the
 * courier (red marker, live lat/lng from the Uber courier_update
 * webhook), the customer pickup pin, and the salon dropoff pin on a
 * Google Map. Shows status + ETA + a fallback link to Uber's own page.
 *
 * Degrades gracefully:
 *   - No Maps key / load failure → renders the Uber tracking link only.
 *   - No courier location yet (webhook hasn't pinged) → shows the
 *     pickup/dropoff pins + "waiting for courier" + the Uber link.
 *
 * Stops polling once the wig is collected/delivered.
 */

import { useEffect, useRef, useState } from "react";
import { setOptions, importLibrary } from "@googlemaps/js-api-loader";
import { MapPin, ExternalLink } from "lucide-react";

interface CourierData {
  status: string | null;
  courierName: string | null;
  courierVehicle: string | null;
  courierLat: number | null;
  courierLng: number | null;
  estimatedPickup: string | null;
  trackingUrl: string | null;
  pickupAddress: string | null;
  dropoffAddress: string | null;
}

let configured = false;
function configureOnce(apiKey: string) {
  if (configured) return;
  setOptions({ key: apiKey, v: "weekly" });
  configured = true;
}

const TERMINAL = new Set(["delivered", "canceled", "returned"]);

export default function CourierTrackMap({
  pickupId,
  trackingUrl,
  className,
}: {
  pickupId: string;
  trackingUrl?: string | null;
  className?: string;
}) {
  const apiKey = process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY;
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<google.maps.Map | null>(null);
  const courierMarkerRef = useRef<google.maps.marker.AdvancedMarkerElement | null>(null);
  const endpointMarkersRef = useRef<google.maps.marker.AdvancedMarkerElement[]>([]);
  const geocoderRef = useRef<google.maps.Geocoder | null>(null);
  const geocodeCache = useRef<Map<string, google.maps.LatLngLiteral>>(new Map());
  const fittedRef = useRef(false);

  const [data, setData] = useState<CourierData | null>(null);
  const [unavailable, setUnavailable] = useState(false);
  const [loadingMap, setLoadingMap] = useState(true);

  const liveTrackingUrl = data?.trackingUrl || trackingUrl || null;

  // Poll the courier endpoint.
  useEffect(() => {
    let stopped = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const poll = async () => {
      try {
        const res = await fetch(`/api/service-pickup/${pickupId}/courier`);
        if (res.ok) {
          const json = await res.json();
          if (!stopped) setData(json.delivery ?? null);
          const st = json.delivery?.status as string | undefined;
          if (st && TERMINAL.has(st)) return; // stop polling when done
        }
      } catch {
        /* keep trying */
      }
      if (!stopped) timer = setTimeout(poll, 15000);
    };
    poll();
    return () => {
      stopped = true;
      if (timer) clearTimeout(timer);
    };
  }, [pickupId]);

  // Init map once.
  useEffect(() => {
    if (!apiKey) {
      setUnavailable(true);
      return;
    }
    configureOnce(apiKey);
    let cancelled = false;
    Promise.all([importLibrary("maps"), importLibrary("marker"), importLibrary("geocoding")])
      .then(([mapsLib, , geocodingLib]) => {
        if (cancelled || !containerRef.current) return;
        const map = new mapsLib.Map(containerRef.current, {
          center: { lat: 40.0, lng: -74.2 },
          zoom: 12,
          mapId: process.env.NEXT_PUBLIC_GOOGLE_MAPS_MAP_ID || "DEMO_MAP_ID",
          disableDefaultUI: true,
          zoomControl: true,
          gestureHandling: "greedy",
        });
        mapRef.current = map;
        geocoderRef.current = new geocodingLib.Geocoder();
        setLoadingMap(false);
      })
      .catch(() => {
        if (cancelled) return;
        setUnavailable(true);
        setLoadingMap(false);
      });
    return () => {
      cancelled = true;
    };
  }, [apiKey]);

  // Render/refresh markers when data changes.
  useEffect(() => {
    const map = mapRef.current;
    const geocoder = geocoderRef.current;
    if (!map || !geocoder || !data) return;
    let cancelled = false;

    (async () => {
      const geocode = async (addr: string): Promise<google.maps.LatLngLiteral | null> => {
        if (geocodeCache.current.has(addr)) return geocodeCache.current.get(addr)!;
        try {
          const res = await geocoder.geocode({ address: addr });
          const loc = res.results[0]?.geometry?.location;
          if (loc) {
            const ll = { lat: loc.lat(), lng: loc.lng() };
            geocodeCache.current.set(addr, ll);
            return ll;
          }
        } catch {
          /* ignore */
        }
        return null;
      };

      const markerLib = (await importLibrary("marker")) as google.maps.MarkerLibrary;
      const pts: google.maps.LatLngLiteral[] = [];

      // Endpoint pins (pickup + dropoff) — render once.
      if (endpointMarkersRef.current.length === 0) {
        const specs = [
          { addr: data.pickupAddress, label: "Pickup", color: "#3b82f6" },
          { addr: data.dropoffAddress, label: "Salon", color: "#10b981" },
        ];
        for (const s of specs) {
          if (!s.addr) continue;
          const pos = await geocode(s.addr);
          if (cancelled || !pos) continue;
          const pin = new markerLib.PinElement({
            background: s.color,
            borderColor: "#fff",
            glyphColor: "#fff",
          });
          const m = new markerLib.AdvancedMarkerElement({
            map,
            position: pos,
            title: s.label,
            content: pin.element,
          });
          endpointMarkersRef.current.push(m);
          pts.push(pos);
        }
      } else {
        for (const [addr, ll] of geocodeCache.current) {
          void addr;
          pts.push(ll);
        }
      }

      // Courier marker (live) — moves each poll.
      if (data.courierLat != null && data.courierLng != null) {
        const pos = { lat: data.courierLat, lng: data.courierLng };
        if (courierMarkerRef.current) {
          courierMarkerRef.current.position = pos;
        } else {
          const pin = new markerLib.PinElement({
            background: "#dc2626",
            borderColor: "#fff",
            glyph: "🚗",
            scale: 1.2,
          });
          courierMarkerRef.current = new markerLib.AdvancedMarkerElement({
            map,
            position: pos,
            title: data.courierName || "Courier",
            content: pin.element,
          });
        }
        pts.push(pos);
      }

      // Fit bounds once (avoid yanking the view on every poll).
      if (!fittedRef.current && pts.length > 0) {
        const bounds = new google.maps.LatLngBounds();
        pts.forEach((p) => bounds.extend(p));
        if (pts.length === 1) {
          map.setCenter(pts[0]);
          map.setZoom(13);
        } else {
          map.fitBounds(bounds, 60);
        }
        fittedRef.current = true;
      } else if (courierMarkerRef.current && data.courierLat != null) {
        // Keep the courier in view as it moves.
        map.panTo({ lat: data.courierLat!, lng: data.courierLng! });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [data]);

  // Status line copy.
  const statusLabel = (() => {
    const s = data?.status;
    if (!s) return "Arranging your courier…";
    if (s === "pending") return "Courier being assigned…";
    if (s === "pickup") return "Courier on the way to you";
    if (s === "pickup_complete") return "Courier has your wig — heading to the salon";
    if (s === "dropoff") return "Arriving at the salon";
    if (s === "delivered") return "Delivered to the salon";
    if (s === "canceled") return "Courier canceled";
    return s;
  })();

  const eta = data?.estimatedPickup ? new Date(data.estimatedPickup) : null;
  const etaLabel =
    eta && data?.status !== "delivered"
      ? eta.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })
      : null;

  // Map unavailable → link-only fallback.
  if (unavailable) {
    return liveTrackingUrl ? (
      <a
        href={liveTrackingUrl}
        target="_blank"
        rel="noopener noreferrer"
        className={`inline-flex items-center gap-1.5 rounded-lg bg-brand-gold px-4 py-2.5 text-sm font-semibold text-white hover:bg-brand-gold/90 transition-colors ${className ?? ""}`}
      >
        <ExternalLink className="h-4 w-4" />
        Track your courier (live)
      </a>
    ) : null;
  }

  return (
    <div className={`rounded-xl border border-gray-200 overflow-hidden bg-white ${className ?? ""}`}>
      <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-gray-100">
        <span className="inline-flex items-center gap-1.5 text-xs font-medium text-brand-dark">
          <MapPin className="h-3.5 w-3.5 text-brand-gold" />
          {statusLabel}
          {data?.courierName ? ` · ${data.courierName}` : ""}
          {etaLabel ? ` · ETA ${etaLabel}` : ""}
        </span>
        {liveTrackingUrl && (
          <a
            href={liveTrackingUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-[11px] font-medium text-brand-gold hover:underline"
          >
            Open in Uber <ExternalLink className="h-3 w-3" />
          </a>
        )}
      </div>
      <div className="relative">
        <div ref={containerRef} className="w-full h-56 sm:h-64 bg-gray-100" />
        {loadingMap && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-100/80 text-xs text-gray-500">
            Loading map…
          </div>
        )}
      </div>
    </div>
  );
}
